import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Play, Flag } from 'lucide-react'
import { Button, Drawer, cn } from '../../design'
import { listStories } from '../../api/stories'
import {
  completeSprint,
  listSprints,
  startSprint,
  updateSprint,
  type CompleteSprint,
  type Sprint,
} from '../../api/sprints'
import type { SprintLookup } from '../../api/users'
import { useAuth } from '../../auth/AuthContext'

const inputCls =
  'w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent'

/**
 * Sprint Planning's lifecycle controls for the selected sprint:
 *   Start sprint — planned → active
 *   End sprint   — close it and roll unfinished stories into the next
 *                  sprint (new or existing) or the backlog
 *   Edit         — name, goal, dates, capacity
 * Viewers see none of them (the API refuses them too).
 */
export function SprintActions({
  projectId,
  sprint,
  onSelect,
}: {
  projectId: number
  sprint: SprintLookup
  onSelect: (id: number) => void
}) {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [ending, setEnding] = useState(false)
  const [editing, setEditing] = useState(false)
  const [key, setKey] = useState(0)

  const fullQ = useQuery({ queryKey: ['sprints', projectId], queryFn: () => listSprints(projectId) })
  const all = fullQ.data ?? []
  const full = all.find(s => s.id === sprint.id) ?? null
  const otherActive = all.find(s => s.status === 'active' && s.id !== sprint.id) ?? null

  const startMut = useMutation({
    mutationFn: () => startSprint(sprint.id),
    onSuccess: () => invalidateSprints(qc, projectId),
  })

  if (!user || user.role === 'viewer' || sprint.status === 'closed') return null
  const today = new Date().toISOString().slice(0, 10)
  const started = sprint.status === 'active' || (!!sprint.start_date && sprint.start_date <= today)

  return (
    <>
      {sprint.status === 'planned' && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Play size={13} />}
          disabled={startMut.isPending || !!otherActive}
          title={otherActive ? `"${otherActive.name}" is active — end it first` : 'Make this the active sprint'}
          onClick={() => startMut.mutate()}
        >
          Start sprint
        </Button>
      )}
      {started && (
        <Button size="sm" variant="outline" leftIcon={<Flag size={13} />} onClick={() => { setKey(k => k + 1); setEnding(true) }}>
          End sprint
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        aria-label="Edit sprint"
        title="Edit sprint"
        disabled={!full}
        onClick={() => { setKey(k => k + 1); setEditing(true) }}
      >
        <Pencil size={14} />
      </Button>
      {startMut.error && <span className="text-xs text-danger">{(startMut.error as Error).message}</span>}

      {full && ending && (
        <EndSprintDrawer
          key={`end-${key}`}
          projectId={projectId}
          sprint={full}
          others={all.filter(s => s.id !== full.id && s.status !== 'closed')}
          onClose={() => setEnding(false)}
          onDone={(nextId) => { invalidateSprints(qc, projectId); if (nextId) onSelect(nextId); setEnding(false) }}
        />
      )}
      {full && editing && (
        <EditSprintDrawer
          key={`edit-${key}`}
          sprint={full}
          onClose={() => setEditing(false)}
          onSaved={() => { invalidateSprints(qc, projectId); setEditing(false) }}
        />
      )}
    </>
  )
}

function invalidateSprints(qc: ReturnType<typeof useQueryClient>, projectId: number) {
  for (const k of [['sprints', projectId], ['lookups', 'sprints', projectId], ['stories'], ['me'], ['timeline']]) {
    qc.invalidateQueries({ queryKey: k })
  }
}

// ─── End sprint ───────────────────────────────────────────────────────────

type Dest = 'new' | 'sprint' | 'backlog'

function EndSprintDrawer({
  projectId,
  sprint,
  others,
  onClose,
  onDone,
}: {
  projectId: number
  sprint: Sprint
  others: Sprint[]
  onClose: () => void
  onDone: (nextSprintId: number | null) => void
}) {
  const openQ = useQuery({
    queryKey: ['stories', 'sprint-open', sprint.id],
    queryFn: () => listStories({ project_id: projectId, sprint_id: sprint.id, per_page: 200 }),
  })
  const open = (openQ.data?.data ?? []).filter(s => s.status_category !== 'done')
  const doneCount = (openQ.data?.data ?? []).length - open.length

  const suggestion = useMemo(() => suggestNext(sprint), [sprint])
  const planned = others.filter(s => s.status === 'planned')
  const [dest, setDest] = useState<Dest>(planned.length ? 'sprint' : 'new')
  const [targetId, setTargetId] = useState<number | null>(planned[0]?.id ?? null)
  const [name, setName] = useState(suggestion.name)
  const [goal, setGoal] = useState('')
  const [startDate, setStartDate] = useState(suggestion.start)
  const [endDate, setEndDate] = useState(suggestion.end)
  const [capacity, setCapacity] = useState(sprint.capacity_points !== null ? String(sprint.capacity_points) : '')
  const [startNext, setStartNext] = useState(true)

  const datesInvalid = !!startDate && !!endDate && endDate < startDate
  const canSubmit = !datesInvalid
    && (dest !== 'new' || name.trim().length > 0)
    && (dest !== 'sprint' || targetId !== null)

  const mut = useMutation({
    mutationFn: () => {
      let body: CompleteSprint
      if (dest === 'backlog') body = { move_to: 'backlog' }
      else if (dest === 'sprint') body = { move_to: 'sprint', target_sprint_id: targetId!, start_next: startNext }
      else body = {
        move_to: 'new',
        start_next: startNext,
        next: {
          name: name.trim(),
          ...(goal.trim() ? { goal: goal.trim() } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(capacity.trim() !== '' ? { capacity_points: Number(capacity) } : {}),
        },
      }
      return completeSprint(sprint.id, body)
    },
    onSuccess: (r) => onDone(r.next?.id ?? null),
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    if (canSubmit) mut.mutate()
  }

  const overdue = sprint.end_date && sprint.end_date < new Date().toISOString().slice(0, 10)

  return (
    <Drawer
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      width="md"
      title={`End ${sprint.name}`}
      description={`${fmt(sprint.start_date)} → ${fmt(sprint.end_date)}${overdue ? ' · past its end date' : ''}`}
      footer={
        <div className="flex items-center gap-2">
          {mut.error && <span className="text-xs text-danger">{(mut.error as Error).message}</span>}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" form="end-sprint-form" disabled={!canSubmit || mut.isPending}>
              {mut.isPending ? 'Ending…' : dest === 'new' && startNext ? 'End & start next' : 'End sprint'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="end-sprint-form" onSubmit={submit} className="space-y-5">
        <div className="rounded-md border border-border bg-bg-muted/40 px-3 py-2.5 text-sm">
          {openQ.isLoading ? 'Counting stories…' : (
            <>
              <span className="font-medium">{doneCount} done</span>
              {' · '}
              <span className={cn('font-medium', open.length > 0 && 'text-warning')}>{open.length} unfinished</span>
              {open.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 text-xs text-fg-muted">
                  {open.slice(0, 8).map(s => (
                    <li key={s.id}><span className="font-mono text-fg-subtle">{s.key}</span> {s.title} <span className="text-fg-subtle">· {s.status_name}</span></li>
                  ))}
                  {open.length > 8 && <li className="text-fg-subtle">+ {open.length - 8} more</li>}
                </ul>
              )}
            </>
          )}
        </div>

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-fg-muted mb-1.5">
            {open.length === 1 ? 'Move the unfinished story to' : 'Move unfinished stories to'}
          </legend>
          <DestOption value="new" dest={dest} setDest={setDest} label="A new sprint" />
          {planned.length > 0 && <DestOption value="sprint" dest={dest} setDest={setDest} label="An existing sprint" />}
          <DestOption value="backlog" dest={dest} setDest={setDest} label="The backlog" />
        </fieldset>

        {dest === 'sprint' && (
          <select className={inputCls} value={targetId ?? ''} onChange={e => setTargetId(Number(e.target.value))} aria-label="Target sprint">
            {planned.map(s => <option key={s.id} value={s.id}>{s.name} · {fmt(s.start_date)} → {fmt(s.end_date)}</option>)}
          </select>
        )}

        {dest === 'new' && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <label className="block">
              <span className="block text-xs font-medium text-fg-muted mb-1.5">Next sprint name<span className="text-danger"> *</span></span>
              <input className={inputCls} value={name} onChange={e => setName(e.target.value)} required />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-fg-muted mb-1.5">Goal</span>
              <input className={inputCls} value={goal} onChange={e => setGoal(e.target.value)} placeholder="What should this sprint deliver?" />
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-fg-muted mb-1.5">Start</span>
                <input type="date" className={inputCls} value={startDate} onChange={e => setStartDate(e.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-fg-muted mb-1.5">End</span>
                <input type="date" className={inputCls} value={endDate} onChange={e => setEndDate(e.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-fg-muted mb-1.5">Capacity (pts)</span>
                <input type="number" min={0} className={inputCls} value={capacity} onChange={e => setCapacity(e.target.value)} />
              </label>
            </div>
            {datesInvalid && <p className="text-xs text-danger">End must be on or after start.</p>}
          </div>
        )}

        {dest !== 'backlog' && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4 accent-accent" checked={startNext} onChange={e => setStartNext(e.target.checked)} />
            Start that sprint now
          </label>
        )}
      </form>
    </Drawer>
  )
}

function DestOption({ value, dest, setDest, label }: { value: Dest; dest: Dest; setDest: (d: Dest) => void; label: string }) {
  return (
    <label className={cn('flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer', dest === value ? 'border-accent bg-accent-soft/40' : 'border-border hover:bg-bg-muted')}>
      <input type="radio" name="dest" className="accent-accent" checked={dest === value} onChange={() => setDest(value)} />
      {label}
    </label>
  )
}

/**
 * Next sprint defaults: the name with its trailing number bumped ("Sprint 4"
 * → "Sprint 5", otherwise "App Release 2"), starting today (or the day after
 * the old end, if that's later) and lasting as long as the old one did
 * (two weeks when it had no dates).
 */
function suggestNext(s: Sprint): { name: string; start: string; end: string } {
  const m = s.name.match(/^(.*?)(\d+)\s*$/)
  const name = m ? `${m[1]}${Number(m[2]) + 1}` : `${s.name} 2`
  const day = 86_400_000
  const lengthDays = s.start_date && s.end_date
    ? Math.max(1, Math.round((Date.parse(s.end_date) - Date.parse(s.start_date)) / day))
    : 13
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const afterOld = s.end_date ? new Date(Date.parse(s.end_date + 'T00:00:00') + day) : today
  const start = afterOld > today ? afterOld : today
  const end = new Date(start.getTime() + lengthDays * day)
  return { name, start: ymd(start), end: ymd(end) }
}

// ─── Edit sprint ──────────────────────────────────────────────────────────

function EditSprintDrawer({ sprint, onClose, onSaved }: { sprint: Sprint; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(sprint.name)
  const [goal, setGoal] = useState(sprint.goal ?? '')
  const [startDate, setStartDate] = useState(sprint.start_date ?? '')
  const [endDate, setEndDate] = useState(sprint.end_date ?? '')
  const [capacity, setCapacity] = useState(sprint.capacity_points !== null ? String(sprint.capacity_points) : '')
  const datesInvalid = !!startDate && !!endDate && endDate < startDate

  const mut = useMutation({
    // Every field is sent; an empty goal, date or capacity clears it.
    mutationFn: () => updateSprint(sprint.id, {
      name: name.trim(),
      goal: goal.trim(),
      start_date: startDate,
      end_date: endDate,
      capacity_points: capacity.trim() === '' ? '' : Number(capacity),
    }),
    onSuccess: onSaved,
  })

  return (
    <Drawer
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      width="md"
      title="Edit sprint"
      description={sprint.status}
      footer={
        <div className="flex items-center gap-2">
          {mut.error && <span className="text-xs text-danger">{(mut.error as Error).message}</span>}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" form="edit-sprint-form" disabled={!name.trim() || datesInvalid || mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="edit-sprint-form" onSubmit={(e) => { e.preventDefault(); mut.mutate() }} className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Name<span className="text-danger"> *</span></span>
          <input className={inputCls} value={name} onChange={e => setName(e.target.value)} required autoFocus />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Goal</span>
          <textarea className={cn(inputCls, 'resize-y')} rows={2} value={goal} onChange={e => setGoal(e.target.value)} />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Start</span>
            <input type="date" className={inputCls} value={startDate} onChange={e => setStartDate(e.target.value)} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">End</span>
            <input type="date" className={inputCls} value={endDate} onChange={e => setEndDate(e.target.value)} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Capacity (pts)</span>
            <input type="number" min={0} className={inputCls} value={capacity} onChange={e => setCapacity(e.target.value)} />
          </label>
        </div>
        {datesInvalid && <p className="text-xs text-danger">End must be on or after start.</p>}
      </form>
    </Drawer>
  )
}

function fmt(d: string | null): string {
  return d ? new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'
}
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
